/**
 * Checkout state persistence
 *
 * Keeps the user's in-progress checkout (form, per-room guest names,
 * "agree to terms" checkbox, loaded booking profile, scroll position,
 * and a short-lived prebook/rate-lock cache) across SPA back-navigation,
 * accidental navigation away, and tab refresh — so returning to /checkout
 * restores the page exactly as the user left it.
 *
 * Scoping: state is bound to the (hotelId + bookHash signature) pair so
 * that switching hotels or rates discards the previous draft.
 *
 * Safety:
 *  - Never persisted past payment success (call clearCheckoutState).
 *  - paymentStep is only persisted while still in the editable 'details' step.
 *  - prebook cache expires automatically with the rate-lock window.
 */

const STATE_KEY = 'checkout_state_v1';
const PREBOOK_KEY = 'checkout_prebook_v1';
const IDENTITY_KEY = 'checkout_identity_v1';
const PREBOOK_CACHE_VERSION = 2;
const MAX_PREBOOK_CACHE_ENTRIES = 12;
/** Stale checkout drafts older than this are dropped on read. */
const STATE_TTL_MS = 24 * 60 * 60 * 1000;
const IDENTITY_TTL_MS = 24 * 60 * 60 * 1000;

export type PersistedRoomGuest = {
  firstName: string;
  lastName: string;
  isChild: boolean;
  age?: number;
};

export interface PersistedCheckoutState {
  /** Signature: `${hotelId}::${sortedBookHashes}` — used to invalidate stale state on hotel/rate change */
  signature: string;
  formData?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    country: string;
    specialRequests: string;
    saveInformation: boolean;
  };
  roomGuests?: PersistedRoomGuest[][];
  termsAgreed?: boolean;
  loadedProfileId?: string | null;
  loadedProfileName?: string | null;
  matchingProfile?: { id: string; name: string; category?: string } | null;
  scrollY?: number;
  /** Saved only while the user is still on the editable details step */
  paymentStep?: 'details';
  /** Wall-clock ms of last write — for debugging / TTL */
  savedAt: number;
}

export interface PersistedPrebook {
  signature: string;
  /** Wall-clock ms when the rate-lock expires */
  expiresAt: number;
  prebookData: any;
  /**
   * Hotel + booking shape captured at the moment of prebook.
   * For multi-room bookings we also stash `bookHashes` and `roomCount`
   * here so that resume is self-sufficient if `multiroom_selection`
   * has been evicted from sessionStorage (e.g. private windows, low storage).
   */
  hotelData?: any & { bookHashes?: string[]; roomCount?: number };
  savedAt: number;
}

type PersistedPrebookCache = {
  version: 2;
  entries: Record<string, PersistedPrebook>;
};

export const buildCheckoutSignature = (
  hotelId: string | undefined | null,
  bookHashes: string[],
): string => {
  const id = (hotelId || '').toString().trim();
  const hashes = [...bookHashes].filter(Boolean).sort().join(',');
  return `${id}::${hashes}`;
};

const stableStringify = (value: any): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
};

const compactHash = (value: string): string => {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
};

const normalizeText = (value: unknown): string => (value ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ');

export const buildCheckoutRateTermsSignature = (details: {
  hotelId?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
  adults?: string | number | null;
  children?: string | number | null;
  rooms?: string | number | null;
  childAges?: string | null;
  roomName?: string | null;
  meal?: any;
  paymentOptions?: any;
  price?: string | number | null;
  currency?: string | null;
}): string | null => {
  const hotelId = normalizeText(details.hotelId);
  const roomName = normalizeText(details.roomName);
  if (!hotelId || !roomName) return null;

  const paymentTypes = Array.isArray(details.paymentOptions?.payment_types)
    ? details.paymentOptions.payment_types.map((paymentType: any) => ({
        currency_code: paymentType?.currency_code ?? null,
        show_currency_code: paymentType?.show_currency_code ?? null,
        type: paymentType?.type ?? null,
        by: paymentType?.by ?? null,
        free_cancellation_before: paymentType?.cancellation_penalties?.free_cancellation_before ?? null,
        penalty_windows: Array.isArray(paymentType?.cancellation_penalties?.policies)
          ? paymentType.cancellation_penalties.policies.map((policy: any) => ({
              start_at: policy?.start_at ?? null,
              end_at: policy?.end_at ?? null,
            }))
          : [],
      }))
    : [];

  const payload = {
    hotelId,
    checkIn: details.checkIn || '',
    checkOut: details.checkOut || '',
    adults: String(details.adults ?? ''),
    children: String(details.children ?? ''),
    rooms: String(details.rooms ?? ''),
    childAges: details.childAges || '',
    roomName,
    meal: typeof details.meal === 'object' ? details.meal : normalizeText(details.meal),
    paymentTypes,
    currency: normalizeText(details.currency),
  };

  return `${hotelId}::terms:${compactHash(stableStringify(payload))}`;
};

export const readCheckoutState = (signature: string): PersistedCheckoutState | null => {
  try {
    const raw = sessionStorage.getItem(STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedCheckoutState;
    if (!parsed || parsed.signature !== signature) return null;
    // Drop stale drafts (>24 h) — prevents very old form data from being shown
    if (parsed.savedAt && Date.now() - parsed.savedAt > STATE_TTL_MS) {
      sessionStorage.removeItem(STATE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const writeCheckoutState = (
  signature: string,
  patch: Partial<Omit<PersistedCheckoutState, 'signature' | 'savedAt'>>,
): void => {
  try {
    const existing = readCheckoutState(signature) || { signature, savedAt: 0 };
    const next: PersistedCheckoutState = {
      ...existing,
      ...patch,
      signature,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(STATE_KEY, JSON.stringify(next));
  } catch {
    /* sessionStorage full or disabled — ignore */
  }
};

export const clearCheckoutState = (): void => {
  try {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(PREBOOK_KEY);
    // Also clear the legacy key so old drafts don't resurrect.
    sessionStorage.removeItem('checkout_primary_contact');
    // Reset the history-seed flag so the next checkout session re-evaluates
    // whether it needs to splice a hotel-details entry behind the back button.
    sessionStorage.removeItem('checkout_history_seeded_v1');
  } catch { /* noop */ }
};

export const readPrebookCache = (signature: string): PersistedPrebook | null => {
  try {
    const raw = sessionStorage.getItem(PREBOOK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedPrebook | PersistedPrebookCache;
    const parsedEntry = (parsed as PersistedPrebookCache)?.version === PREBOOK_CACHE_VERSION
      ? (parsed as PersistedPrebookCache).entries?.[signature]
      : (parsed as PersistedPrebook);
    if (!parsedEntry || parsedEntry.signature !== signature) return null;
    // Need at least 30 s of headroom for the rate to be considered "fresh"
    if (parsedEntry.expiresAt - Date.now() < 30_000) return null;
    return parsedEntry;
  } catch {
    return null;
  }
};

export const writePrebookCache = (
  signature: string,
  prebookData: any,
  expiresAtMs: number,
  hotelData?: any,
): void => {
  try {
    const raw = sessionStorage.getItem(PREBOOK_KEY);
    const parsed = raw ? JSON.parse(raw) as PersistedPrebook | PersistedPrebookCache : null;
    const entries: Record<string, PersistedPrebook> = (parsed as PersistedPrebookCache)?.version === PREBOOK_CACHE_VERSION
      ? { ...((parsed as PersistedPrebookCache).entries || {}) }
      : (parsed as PersistedPrebook)?.signature
        ? { [(parsed as PersistedPrebook).signature]: parsed as PersistedPrebook }
        : {};
    const payload: PersistedPrebook = {
      signature,
      expiresAt: expiresAtMs,
      prebookData,
      hotelData,
      savedAt: Date.now(),
    };
    entries[signature] = payload;
    const freshEntries = Object.fromEntries(
      Object.entries(entries)
        .filter(([, entry]) => entry.expiresAt - Date.now() >= 30_000)
        .sort(([, a], [, b]) => b.savedAt - a.savedAt)
        .slice(0, MAX_PREBOOK_CACHE_ENTRIES)
    );
    sessionStorage.setItem(PREBOOK_KEY, JSON.stringify({ version: PREBOOK_CACHE_VERSION, entries: freshEntries }));
  } catch { /* noop */ }
};

/**
 * Identity draft — name/email/phone/country, kept across signature changes
 * so switching room or dates within the same session keeps the traveller's
 * identity populated. Per-room guests, special requests and terms reset.
 *
 * Lives in sessionStorage with a 24 h hard TTL. Cleared on logout.
 */
export interface PersistedIdentityDraft {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  savedAt: number;
}

export const readIdentityDraft = (): PersistedIdentityDraft | null => {
  try {
    const raw = sessionStorage.getItem(IDENTITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedIdentityDraft;
    if (!parsed) return null;
    if (parsed.savedAt && Date.now() - parsed.savedAt > IDENTITY_TTL_MS) {
      sessionStorage.removeItem(IDENTITY_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const writeIdentityDraft = (
  identity: Omit<PersistedIdentityDraft, 'savedAt'>,
): void => {
  try {
    // Skip writing an empty record — avoids stomping a previously-saved draft
    // during the brief mount window before the form has hydrated.
    const hasAny = !!(
      identity.firstName?.trim() ||
      identity.lastName?.trim() ||
      identity.email?.trim() ||
      identity.phone?.trim() ||
      identity.country?.trim()
    );
    if (!hasAny) return;
    const payload: PersistedIdentityDraft = { ...identity, savedAt: Date.now() };
    sessionStorage.setItem(IDENTITY_KEY, JSON.stringify(payload));
  } catch { /* noop */ }
};

/**
 * Clear all checkout-related sessionStorage on logout.
 * Prevents one user's draft / rate lock leaking to the next user
 * on a shared device.
 */
export const clearCheckoutOnLogout = (): void => {
  try {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(PREBOOK_KEY);
    sessionStorage.removeItem(IDENTITY_KEY);
    sessionStorage.removeItem('checkout_primary_contact');
    sessionStorage.removeItem('checkout_history_seeded_v1');
  } catch { /* noop */ }
};
